import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Download,
  Maximize,
  Minimize,
  BookOpen,
  Loader2,
  AlertCircle
} from "lucide-react";
import mammoth from "mammoth";
import DOMPurify from "dompurify";
import { fetchDocumentData } from "../utils/documentStore";

/**
 * DocumentReaderModal
 * 
 * Clean, full-screen presentation & document reader in Light Mode.
 * Features:
 * - Smart slide pagination keeping headings and bullet groups together
 * - Styled bullet lists, bold hierarchy, and callout cards
 * - Page progress, word counts, and keyboard navigation
 * - Unified action bar with Zoom / Size Resizer, Download, Fullscreen, and Close
 */
const DocumentReaderModal = ({
  url,
  mediaId,
  filename,
  cachedData,
  cachedSlides,
  onClose,
  initialPage = 1,
}) => {
  const [loading, setLoading] = useState(!cachedSlides || cachedSlides.length === 0);
  const [error, setError] = useState(null);
  const [slides, setSlides] = useState(cachedSlides || []);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(Math.max(0, initialPage - 1));
  const [scale, setScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [totalWordCount, setTotalWordCount] = useState(0);
  const [downloadBlobUrl, setDownloadBlobUrl] = useState(cachedData?.blobUrl || null);

  const containerRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // ── Reset Scroll to Top on Slide Change ────────────────────────────
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [currentSlideIndex]);

  // ── Load & Parse Document ──────────────────────────────────────────
  useEffect(() => {
    if (cachedSlides && cachedSlides.length > 0) {
      setSlides(cachedSlides);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadDocument = async () => {
      try {
        setLoading(true);
        setError(null);

        let arrayBuffer;
        if (cachedData?.arrayBuffer) {
          arrayBuffer = cachedData.arrayBuffer;
          setDownloadBlobUrl(cachedData.blobUrl);
        } else {
          const result = await fetchDocumentData({
            mediaId,
            url,
            mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          });
          arrayBuffer = result.arrayBuffer;
          setDownloadBlobUrl(result.blobUrl);
        }

        const result = await mammoth.convertToHtml({ arrayBuffer });
        if (cancelled) return;

        const rawHtml = result.value || "";
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<div>${rawHtml}</div>`, "text/html");
        const root = doc.body.firstElementChild;
        const nodes = Array.from(root?.children || []);

        const extractedSlides = [];
        let currentSlideNodes = [];
        let currentWordCount = 0;
        let totalWords = 0;

        const commitSlide = () => {
          if (currentSlideNodes.length === 0) return;
          const slideWrapper = document.createElement("div");
          currentSlideNodes.forEach((n) => slideWrapper.appendChild(n.cloneNode(true)));
          extractedSlides.push(slideWrapper.innerHTML);
          currentSlideNodes = [];
          currentWordCount = 0;
        };

        nodes.forEach((el) => {
          const text = (el.textContent || "").trim();
          if (!text) return;

          const words = text.split(/\s+/).filter(Boolean).length;
          totalWords += words;

          const tagName = el.tagName.toUpperCase();
          const isH1 = tagName === "H1";
          const isH2 = tagName === "H2";
          const hasPageBreak = el.innerHTML.includes("page-break") || tagName === "HR";

          // Format "Key point:" or "Note:" paragraphs with callout styling
          if (
            text.toLowerCase().startsWith("key point:") ||
            text.toLowerCase().startsWith("key takeaway:") ||
            text.toLowerCase().startsWith("note:")
          ) {
            el.className = "reader-callout bg-indigo-50/80 border border-indigo-200 text-indigo-950 p-4 rounded-xl my-3";
            const inner = el.innerHTML;
            el.innerHTML = inner.replace(
              /^(key point:|key takeaway:|note:)/i,
              '<strong class="text-indigo-700 font-bold">$1</strong>'
            );
          }

          // Smart slide boundary: H1, or major H2 with content, or max words reached
          const shouldBreak =
            currentSlideNodes.length > 0 &&
            (hasPageBreak || isH1 || (isH2 && currentWordCount > 60) || currentWordCount + words > 240);

          if (shouldBreak) {
            commitSlide();
          }

          currentSlideNodes.push(el);
          currentWordCount += words;
        });

        commitSlide();

        // Fallback: if slide boundaries extracted nothing, use the full sanitized HTML as one slide
        const finalSlides = extractedSlides.length > 0 ? extractedSlides : (rawHtml ? [rawHtml] : ["<p class='text-gray-400 italic'>Empty document</p>"]);
        setSlides(finalSlides);
        setTotalWordCount(totalWords);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to parse document");
          setLoading(false);
        }
      }
    };

    loadDocument();
    return () => {
      cancelled = true;
    };
  }, [cachedData, cachedSlides, mediaId, url]);

  // ── Keyboard Navigation ─────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        setCurrentSlideIndex((prev) => Math.max(0, prev - 1));
      } else if (e.key === "ArrowRight") {
        setCurrentSlideIndex((prev) => Math.min(slides.length - 1, prev + 1));
      } else if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [slides.length, onClose]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  // Sync fullscreen state when exiting via Escape key or browser chrome
  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  // ── Touch Swipe Navigation ──
  const touchStartRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!touchStartRef.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    const dt = Date.now() - touchStartRef.current.time;
    touchStartRef.current = null;

    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 500) {
      if (dx < 0) {
        setCurrentSlideIndex((prev) => Math.min(slides.length - 1, prev + 1));
      } else {
        setCurrentSlideIndex((prev) => Math.max(0, prev - 1));
      }
    }
  }, [slides.length]);

  const currentSlideHtml = slides[currentSlideIndex] || "";
  const totalSlides = slides.length || 1;
  const currentSlideNumber = currentSlideIndex + 1;
  const finalDownloadUrl = downloadBlobUrl || url;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[100] flex flex-col bg-[#F5F7FA] select-none transition-colors duration-200"
    >
      {/* ── Top Header Bar ── */}
      <header className="flex items-center justify-between px-5 sm:px-6 py-3 bg-white border-b border-gray-200 text-gray-800 shrink-0 shadow-xs">
        <div className="flex items-center gap-2.5 min-w-0">
          <BookOpen size={18} className="text-indigo-600 shrink-0" />
          <h2 className="text-sm font-bold text-gray-900 truncate max-w-[280px] sm:max-w-md md:max-w-lg" title={filename}>
            {filename}
          </h2>
        </div>

        {/* Middle / Size Resizer Control */}
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex items-center bg-gray-100 border border-gray-200 rounded-xl p-0.5">
            <button
              type="button"
              onClick={() => setScale((s) => Math.max(0.7, +(s - 0.1).toFixed(1)))}
              title="Decrease text size"
              className="p-1.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-white transition-all"
            >
              <ZoomOut size={15} />
            </button>
            <span className="text-xs font-semibold text-gray-700 font-mono w-11 text-center">
              {Math.round(scale * 100)}%
            </span>
            <button
              type="button"
              onClick={() => setScale((s) => Math.min(1.8, +(s + 0.1).toFixed(1)))}
              title="Increase text size"
              className="p-1.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-white transition-all"
            >
              <ZoomIn size={15} />
            </button>
            <button
              type="button"
              onClick={() => setScale(1)}
              title="Reset text size"
              className="px-2 py-1 text-[11px] font-semibold rounded-lg text-gray-600 hover:text-gray-900 hover:bg-white transition-all"
            >
              Reset
            </button>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <a
            href={finalDownloadUrl}
            download={filename}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors flex items-center justify-center"
            title="Download Document"
          >
            <Download size={16} />
          </a>

          <button
            type="button"
            onClick={toggleFullscreen}
            className="p-2 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors flex items-center justify-center"
            title={isFullscreen ? "Exit Fullscreen" : "Toggle Fullscreen"}
          >
            {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
          </button>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-500 hover:text-red-600 transition-colors ml-1 flex items-center justify-center"
            title="Close Viewer"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>
      </header>

      {/* ── Main Body: Full Width Slide Canvas ── */}
      <div
        className="flex flex-1 overflow-hidden relative"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <main
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto px-3 sm:px-8 md:px-16 py-4 sm:py-6 md:py-10 flex flex-col items-center justify-start relative"
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center p-16 opacity-75 my-auto">
              <Loader2 size={36} className="animate-spin text-indigo-600 mb-3" />
              <span className="text-sm font-medium text-gray-600">Preparing document slides…</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center p-12 text-red-600 gap-3 max-w-md text-center my-auto bg-white rounded-2xl border border-red-200 shadow-sm">
              <AlertCircle size={40} className="text-red-500" />
              <p className="text-sm font-semibold">{error}</p>
              <a
                href={finalDownloadUrl}
                download={filename}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-semibold hover:bg-indigo-700 transition-colors shadow-sm"
              >
                Download File Instead
              </a>
            </div>
          ) : (
            <div className="w-full max-w-4xl flex flex-col items-center pb-6">
              {/* Slide Card */}
              <div
                className="w-full rounded-2xl p-6 sm:p-10 md:p-12 border border-gray-200 bg-white shadow-sm transition-all duration-200 min-h-[460px] flex flex-col justify-start text-gray-900"
              >
                <div
                  className="document-prose reader-light text-base leading-relaxed text-gray-900 transition-all duration-150"
                  style={{ fontSize: `${scale * 100}%` }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentSlideHtml) }}
                />
              </div>
            </div>
          )}
        </main>

        {/* Floating Left Arrow Button */}
        {currentSlideIndex > 0 && (
          <button
            type="button"
            onClick={() => setCurrentSlideIndex((prev) => Math.max(0, prev - 1))}
            className="absolute left-1.5 sm:left-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-11 sm:h-11 rounded-full bg-white/95 hover:bg-white active:bg-gray-100 text-gray-700 flex items-center justify-center shadow-lg border border-gray-200 transition-all hover:scale-105 active:scale-95 z-30"
            title="Previous Slide (Left Arrow)"
          >
            <ChevronLeft className="w-4 h-4 sm:w-6 sm:h-6" />
          </button>
        )}

        {/* Floating Right Arrow Button */}
        {currentSlideIndex < totalSlides - 1 && (
          <button
            type="button"
            onClick={() => setCurrentSlideIndex((prev) => Math.min(totalSlides - 1, prev + 1))}
            className="absolute right-1.5 sm:right-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-11 sm:h-11 rounded-full bg-white/95 hover:bg-white active:bg-gray-100 text-gray-700 flex items-center justify-center shadow-lg border border-gray-200 transition-all hover:scale-105 active:scale-95 z-30"
            title="Next Slide (Right Arrow)"
          >
            <ChevronRight className="w-4 h-4 sm:w-6 sm:h-6" />
          </button>
        )}
      </div>

      {/* ── Bottom Navigation & Stats Bar ── */}
      <footer className="flex items-center justify-between px-3 sm:px-8 py-2 sm:py-3 bg-white border-t border-gray-200 shrink-0 text-xs text-gray-600 shadow-xs">
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="font-bold text-gray-900">
            <span className="hidden sm:inline">Page </span>{currentSlideNumber}<span className="text-gray-400 font-medium">/</span>{totalSlides}
          </span>
          {totalWordCount > 0 && (
            <span className="text-gray-400 hidden md:inline">
              • {totalWordCount} words
            </span>
          )}
        </div>

        {/* Progress Bar — visible on all screens */}
        <div className="flex-1 max-w-[100px] sm:max-w-xs mx-3 sm:mx-6 flex items-center gap-2">
          <div className="flex-1 h-1 sm:h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-600 rounded-full transition-all duration-300"
              style={{ width: `${(currentSlideNumber / totalSlides) * 100}%` }}
            />
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            type="button"
            onClick={() => setCurrentSlideIndex((prev) => Math.max(0, prev - 1))}
            disabled={currentSlideIndex <= 0}
            className="px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs flex items-center gap-0.5"
          >
            <ChevronLeft size={14} /> <span className="hidden sm:inline">Prev</span>
          </button>
          <button
            type="button"
            onClick={() => setCurrentSlideIndex((prev) => Math.min(totalSlides - 1, prev + 1))}
            disabled={currentSlideIndex >= totalSlides - 1}
            className="px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs flex items-center gap-0.5"
          >
            <span className="hidden sm:inline">Next</span> <ChevronRight size={14} />
          </button>
        </div>
      </footer>
    </div>
  );
};

export default DocumentReaderModal;
